import { Accordion } from "./core/accordion";
import { AccordionItem } from "./core/accordion-item";
import {
  useCurrentChannel,
  useChannelUiGroups,
  useSession,
  useChannels,
  useSdk,
} from "./session-provider";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { FunctionComponent } from "preact";
import {
  ChannelPickerGroup,
  getChannelDisabledReason,
} from "./channel-picker-group";
import { assert, satisfiesMinMax } from "../utils";
import { BffSession } from "../backend-types/session";
import { BffChannel, BffChannelUiGroup } from "../backend-types/channel";
import { TFunction } from "../localization";
import {
  findChannelPairs,
  getChannelCodesForTelemetry,
  makeChannelsByGroupId,
  singleBffChannelToPublic,
} from "../bff-marshal";
import { ChannelPickerDigitalWalletSection } from "./channel-picker-digital-wallet-section";
import { getTelemetry, SessionTelemetryScope } from "../telemetry";
import { TelemetryEvents } from "../telemetry-events";

type Props = object;

export const ChannelPickerRoot: FunctionComponent<Props> = (props) => {
  const sdk = useSdk();
  const telemetry = getTelemetry(sdk);
  const session = useSession();
  const channelUiGroups = useChannelUiGroups();
  const currentChannel = useCurrentChannel();
  const channels = useChannels();
  const { t } = useSdk();

  const channelsByGroup = useMemo(() => {
    return makeChannelsByGroupId(channels, {
      options: { filterMinMax: false },
      pairChannels: findChannelPairs(channels),
      session,
    });
  }, [channels, session]);

  const thisRef = useRef<HTMLDivElement>(null);

  const pairChannelData = useMemo(() => findChannelPairs(channels), [channels]);
  const marshalConfig = useMemo(
    () => ({
      pairChannels: pairChannelData,
      session: {
        amount: session.amount,
        session_type: session.session_type,
      },
      options: { filterMinMax: false },
    }),
    [pairChannelData, session.amount, session.session_type],
  );

  // selected group is the containing group of the currently selected channel
  const selectedGroupId = currentChannel?.ui_group ?? null;

  // previewed group means expanded but no channel selected
  const [previewGroupId, setPreviewGroupId] = useState<string | null>(null);

  const telemetryScopeForGroup = useRef<SessionTelemetryScope | null>(null);
  const telemetryForGroupClear = useCallback(() => {
    if (telemetryScopeForGroup.current) {
      telemetry.popScope(telemetryScopeForGroup.current);
      telemetryScopeForGroup.current = null;
    }
  }, [telemetry]);
  const telemetryForGroupChange = useCallback(
    (groupName: string, groupId: string) => {
      telemetryForGroupClear();
      const channelList = getChannelCodesForTelemetry(
        session,
        channels,
        groupId,
      );
      telemetryScopeForGroup.current = telemetry.appendAndPushScope(
        TelemetryEvents.ChannelGroup(true, groupName, channelList),
      );
    },
    [telemetry, telemetryForGroupClear, session, channels],
  );

  const handleSelectChannelGroup = useCallback(
    (groupId: string) => {
      if (selectedGroupId === groupId || previewGroupId === groupId) {
        // user wants to collapse the group while a channel was selected, clear the channel selection
        if (selectedGroupId === groupId) {
          // clear actual selection
          thisRef.current?.dispatchEvent(
            new XenditClearCurrentChannelEvent(groupId),
          );
          telemetryForGroupClear();
        }
        if (previewGroupId === groupId) {
          // clear previewed state
          setPreviewGroupId(null);
          telemetryForGroupClear();
        }
      } else {
        // user wants to open a different group
        // if the new group has one channel, select it automatically
        // otherwise set it as previewed
        const newGroup = channelUiGroups.find((g) => g.id === groupId);
        assert(newGroup);
        const enabledChannels = groupEnabledChannelStats(
          session,
          newGroup,
          channels,
          t,
        ).enabledChannels;
        if (enabledChannels === 0) {
          // no enabled channels, do nothing
          return;
        } else if (enabledChannels === 1) {
          // one enabled channel, select it automatically
          const ch = channelsByGroup[groupId][0];
          telemetryForGroupChange(newGroup.label, groupId);
          sdk.setCurrentChannel(singleBffChannelToPublic(ch, marshalConfig));
          setPreviewGroupId(null);
        } else {
          // multiple enabled channels, set as previewed and clear the channel selection
          telemetryForGroupChange(newGroup.label, groupId);
          setPreviewGroupId(groupId);
          sdk.setCurrentChannel(null);
        }
      }
    },
    [
      channelUiGroups,
      channels,
      channelsByGroup,
      marshalConfig,
      previewGroupId,
      sdk,
      selectedGroupId,
      session,
      t,
      telemetryForGroupChange,
      telemetryForGroupClear,
    ],
  );

  // once a channel is selected, remove previewGroupId.
  // (without this, the group would continue showing the old selected channel after the selection is cleared using setCurrentChannel(null))
  useLayoutEffect(() => {
    if (currentChannel !== null && previewGroupId !== null) {
      setPreviewGroupId(null);
    }
  }, [currentChannel, previewGroupId]);

  return (
    <div ref={thisRef}>
      <ChannelPickerDigitalWalletSection />
      <Accordion>
        {channelUiGroups
          .filter((group) => {
            // remove empty groups
            return (channelsByGroup[group.id] || []).length > 0;
          })
          .map((group) => {
            // make the group open if it is selected or previewed
            const open =
              selectedGroupId !== null
                ? selectedGroupId === group.id
                : previewGroupId === group.id;

            // make the group disabled if it has no enabled channels
            const enabledChannelsStats = groupEnabledChannelStats(
              session,
              group,
              channels,
              t,
            );
            const disabled = enabledChannelsStats.enabledChannels === 0;
            const disabledReason =
              enabledChannelsStats.firstDisabledChannelReason;

            const channelLogos = resolveChannelLogosForGroup(
              session,
              channelsByGroup[group.id],
            );

            return (
              <AccordionItem
                key={group.id}
                id={group.id}
                title={group.label}
                subtitle={disabledReason ?? undefined}
                open={open}
                disabled={disabled}
                onClick={handleSelectChannelGroup}
                channelLogos={channelLogos}
              >
                <ChannelPickerGroup group={group} open={open} />
              </AccordionItem>
            );
          })}
      </Accordion>
    </div>
  );
};

// returns null if the group has any enabled channels, otherwise returns the disabled reason as a string
function groupEnabledChannelStats(
  session: BffSession,
  group: BffChannelUiGroup,
  channels: BffChannel[],
  t: TFunction,
): {
  enabledChannels: number;
  firstDisabledChannelReason: string | null;
} {
  let firstDisabledChannelReason = null;
  let enabledChannels = 0;
  for (const channel of channels) {
    if (channel.ui_group !== group.id) continue;
    if (satisfiesMinMax(session, channel)) {
      enabledChannels++;
      continue;
    }
    if (firstDisabledChannelReason === null) {
      firstDisabledChannelReason = getChannelDisabledReason(
        t,
        session,
        channel,
      );
    }
  }
  return {
    enabledChannels,
    firstDisabledChannelReason,
  };
}

function resolveChannelLogosForGroup(
  session: BffSession,
  channels: BffChannel[],
): { src: string; alt: string; enabled: boolean }[] {
  const logos: { src: string; alt: string; enabled: boolean }[] = [];
  for (const channel of channels) {
    const enabled = satisfiesMinMax(session, channel);
    if (channel.card) {
      // use generic card icon
      logos.push({
        src: "https://assets.xendit.co/payment-session/logos/CARDS.svg",
        alt: channel.brand_name,
        enabled,
      });
    } else {
      // else use channel brand logo
      logos.push({
        src: channel.brand_logo_url,
        alt: channel.brand_name,
        enabled,
      });
    }
  }
  return logos;
}

export class XenditClearCurrentChannelEvent extends Event {
  static readonly type = "xendit-clear-current-channel" as const;
  uiGroup: string;

  constructor(uiGroup: string) {
    super(XenditClearCurrentChannelEvent.type, {
      bubbles: true,
      composed: true,
    });
    this.uiGroup = uiGroup;
  }
}
